import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { cn } from '../../utils/cn';

// Encode mermaid text to a mermaid.ink URL without adding a heavy mermaid runtime dependency.
const mermaidToImageUrl = (code: string) => {
  try {
    // mermaid.ink expects base64 of the raw text
    return `https://mermaid.ink/img/${btoa(unescape(encodeURIComponent(code)))}`;
  } catch {
    return '';
  }
};

interface MarkdownContentProps {
  content: string;
  className?: string;
}

// ─── Inline citation button ──────────────────────────────────────────────────
// Encoded as `curious-citation:N:ref` inline code by toInteractiveMessageContent.
// We render as a button here so no link/URL sanitisation is involved at all.
const CitationTag: React.FC<{ index: string; citationRef: string }> = ({
  index,
  citationRef,
}) => (
  <button
    type="button"
    title={`Source: ${citationRef}`}
    className="mx-0.5 inline-flex items-center gap-1 rounded-md border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 shadow-sm transition-all duration-150 hover:border-blue-300 hover:from-blue-100 hover:to-indigo-100 hover:shadow-md active:scale-95 cursor-pointer"
    onClick={() => {
      window.dispatchEvent(
        new CustomEvent('curious:insights-open', { detail: { tab: 'context' } }),
      );
      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('curious:scroll-to-source', {
            detail: { citation_ref: citationRef },
          }),
        );
      }, 80);
    }}
  >
    {/* Book / source icon */}
    <svg
      className="w-2.5 h-2.5 text-blue-500 shrink-0"
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
    </svg>
    <span>Source {index}</span>
  </button>
);

// ─── Inline clarification / needs-input button ────────────────────────────────
const NeedsInputTag: React.FC<{ description: string }> = ({ description }) => {
  const shortDesc =
    description.length > 40 ? description.slice(0, 40) + '…' : description;
  return (
    <button
      type="button"
      title={description}
      className="mx-0.5 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800 shadow-sm transition-all duration-150 hover:border-amber-400 hover:from-amber-100 hover:to-orange-100 hover:shadow-md active:scale-95 cursor-pointer"
      onClick={() => {
        window.dispatchEvent(
          new CustomEvent('curious:fill-input', {
            detail: { text: `Re: "${description}" — ` },
          }),
        );
      }}
    >
      {/* Question / clarify icon */}
      <svg
        className="w-2.5 h-2.5 text-amber-500 shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
          clipRule="evenodd"
        />
      </svg>
      <span className="text-amber-600 font-medium">Clarify:</span>
      <span className="font-normal opacity-90">{shortDesc}</span>
    </button>
  );
};

export const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, className }) => {
  return (
    <div
      className={cn(
        'prose prose-sm max-w-none',
        // Headings
        'prose-headings:font-semibold prose-headings:text-neutral-900 prose-headings:mt-4 prose-headings:mb-2',
        'prose-h1:text-xl prose-h2:text-lg prose-h3:text-base',
        // Paragraphs
        'prose-p:text-neutral-800 prose-p:leading-relaxed prose-p:my-3 prose-p:first:mt-0 prose-p:last:mb-0',
        // Lists
        'prose-ul:my-3 prose-ul:pl-6 prose-ul:list-disc',
        'prose-ol:my-3 prose-ol:pl-6 prose-ol:list-decimal',
        'prose-li:my-1.5 prose-li:leading-relaxed prose-li:pl-1',
        // Strong and emphasis
        'prose-strong:text-neutral-900 prose-strong:font-semibold',
        'prose-em:text-neutral-700 prose-em:italic',
        // Code — the curious-* inline tags override these styles via component renderer below
        'prose-code:text-blue-600 prose-code:bg-blue-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono prose-code:before:content-none prose-code:after:content-none',
        'prose-pre:bg-neutral-50 prose-pre:border prose-pre:border-neutral-200 prose-pre:rounded-lg prose-pre:p-4 prose-pre:overflow-x-auto prose-pre:my-4',
        'prose-pre:code:bg-transparent prose-pre:code:p-0 prose-pre:code:text-sm',
        // Blockquotes
        'prose-blockquote:border-l-4 prose-blockquote:border-neutral-300 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-neutral-600 prose-blockquote:my-4',
        // Links
        'prose-a:text-blue-600 prose-a:underline prose-a:hover:text-blue-700 prose-a:font-medium',
        // Horizontal rules
        'prose-hr:border-neutral-200 prose-hr:my-4',
        // Tables
        'prose-table:my-4 prose-th:font-semibold prose-th:text-neutral-900 prose-td:text-neutral-700',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          // ── External links: open in new tab ───────────────────────────────
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          a({ href, children, ...props }: any) {
            return (
              <a href={String(href || '')} target="_blank" rel="noreferrer noopener" {...props}>
                {children}
              </a>
            );
          },

          // ── Code spans and blocks ─────────────────────────────────────────
          // curious-citation:N:ref  → blue source tag  (inline, no className)
          // curious-needs-input:desc → amber clarify tag (inline, no className)
          // language-mermaid        → rendered via mermaid.ink
          // everything else         → default <code>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          code({ className, children, ...props }: any) {
            const raw = String(children || '').trim();
            const language = (className || '').replace('language-', '');

            // ── Curious inline tags (no className = inline code span) ────────
            if (!className) {
              if (raw.startsWith('curious-citation:')) {
                // Format: curious-citation:N:citation-ref
                const withoutPrefix = raw.slice('curious-citation:'.length);
                const firstColon = withoutPrefix.indexOf(':');
                if (firstColon !== -1) {
                  const index = withoutPrefix.slice(0, firstColon);
                  const citationRef = withoutPrefix.slice(firstColon + 1);
                  return <CitationTag index={index} citationRef={citationRef} />;
                }
              }

              if (raw.startsWith('curious-needs-input:')) {
                const description = raw.slice('curious-needs-input:'.length);
                return <NeedsInputTag description={description} />;
              }
            }

            // ── Mermaid diagrams ─────────────────────────────────────────────
            if (language === 'mermaid') {
              const url = mermaidToImageUrl(raw);
              return url ? (
                <img
                  src={url}
                  alt="Mermaid diagram"
                  className="my-4 rounded border border-neutral-200 bg-white"
                />
              ) : (
                <pre className={className as string} {...props}>
                  {children}
                </pre>
              );
            }

            // ── Default code rendering ───────────────────────────────────────
            return (
              <code className={className as string} {...props}>
                {children}
              </code>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
